import type { PickemQuestion } from '@low6-pickem/api-types';
import { QUESTION_TYPES } from '@/constants/question-types';

/**
 * Checks if a question is a tiebreaker question
 *
 * @param question - The question to check
 * @returns True if the question is a tiebreaker, false otherwise
 *
 * @example
 * ```ts
 * const isTiebreaker = isTiebreakerQuestion(question);
 * if (isTiebreaker) {
 *   // Handle tiebreaker differently
 * }
 * ```
 */
export function isTiebreakerQuestion(question: PickemQuestion): boolean {
  return question.question_type?.toLowerCase() === QUESTION_TYPES.TIEBREAKER;
}

/**
 * Sorts questions with tiebreaker questions placed last
 *
 * Regular questions are sorted by their `order` field (ascending).
 * Tiebreaker questions are always sorted to the end, regardless of their order value.
 *
 * This function creates a new array and does not mutate the original.
 *
 * @param questions - Array of questions to sort
 * @returns A new array with questions sorted (tiebreakers last)
 *
 * @example
 * ```ts
 * const sorted = sortQuestionsWithTiebreakerLast(pickem.questions);
 * // Regular questions appear first, sorted by order
 * // Tiebreaker questions appear last
 * ```
 */
export function sortQuestionsWithTiebreakerLast(questions: PickemQuestion[]): PickemQuestion[] {
  return questions.slice().sort((a, b) => {
    const aIsTiebreaker = isTiebreakerQuestion(a);
    const bIsTiebreaker = isTiebreakerQuestion(b);

    // Tiebreakers go to the end
    if (aIsTiebreaker && !bIsTiebreaker) return 1;
    if (!aIsTiebreaker && bIsTiebreaker) return -1;

    // Both are regular questions or both are tiebreakers - sort by order
    return (a.order || 0) - (b.order || 0);
  });
}

/**
 * Filters out tiebreaker questions, returning only regular questions
 *
 * @param questions - Array of questions to filter
 * @returns A new array containing only non-tiebreaker questions
 *
 * @example
 * ```ts
 * const regularQuestions = getRegularQuestions(pickem.questions);
 * // Returns only team, player, and prop questions (no tiebreakers)
 * ```
 */
export function getRegularQuestions(questions: PickemQuestion[]): PickemQuestion[] {
  return questions.filter(q => !isTiebreakerQuestion(q));
}

/**
 * Gets only tiebreaker questions from an array of questions
 *
 * @param questions - Array of questions to filter
 * @returns A new array containing only tiebreaker questions
 *
 * @example
 * ```ts
 * const tiebreakers = getTiebreakerQuestions(pickem.questions);
 * // Returns only tiebreaker questions
 * ```
 */
export function getTiebreakerQuestions(questions: PickemQuestion[]): PickemQuestion[] {
  return questions.filter(q => isTiebreakerQuestion(q));
}
